import { CalculContextState, ProjectContextState } from '@batical/types/src'
import sendError from '../ErrorReporter'
import { once, orderByChild, update } from './database'
import { UserState } from '@batical/reducer/src'
import { firestoreSubscriptionData, setFirestoreSubscriptionData, updateFirestoreSubscriptionData } from './firestore'
import { uploadFile } from './storage'

export async function saveCalcul(
  context: CalculContextState | ProjectContextState,
  name: string,
  saveDate: number,
  userId: string,
  isProject?: boolean
): Promise<void> {
  try {
    const ctx = { ...context }
    ctx.name = name
    ctx.saveDate = saveDate
    ctx.deleted = 0

    let refBackup = ''
    if (isProject) {
      refBackup = `users/${userId}/backupProjects/${(ctx as ProjectContextState).calcul_code + '_' + name.toLowerCase()}`
    } else {
      refBackup = `users/${userId}/backup/${(ctx as CalculContextState).calcul_code + '_' + name.toLowerCase()}`
    }
    await update(refBackup, ctx)
  } catch (error) {
    throw error
  }
}

export async function calculExist(code: string, name: string, userid: string, isProject?: boolean): Promise<boolean> {
  try {
    let urbackup
    if (isProject) {
      urbackup = `users/${userid}/backupProjects/${code}_${name.toLowerCase()}`
    } else {
      urbackup = `users/${userid}/backup/${code}_${name.toLowerCase()}`
    }

    const snapshot = await once(urbackup)

    const aved = snapshot.val()
    if (aved == null) return false
    if (aved.deleted === 1) return false
    return true
  } catch (error) {
    sendError(error)
    throw error
  }
}

export async function fetchBackup(userId: string): Promise<Array<CalculContextState>> {
  try {
    const snapshot = await orderByChild(`users/${userId}/backup/`, 'saveDate')

    const res: Array<CalculContextState> = []
    snapshot.forEach(element => {
      const result = element.val()
      if (result.deleted !== 1 && result.calcul_code) {
        //avoid mix with project
        result.key_backup = element.key
        result.calcul_code = result.calcul_code.replace('_', '')
        res.unshift(result)
      }
    })
    return res
  } catch (error) {
    sendError(error)
    throw error
  }
}

export async function fetchBackupProjects(userId: string): Promise<Array<ProjectContextState>> {
  try {
    const res: Array<ProjectContextState> = []
    const snapshot = await orderByChild(`users/${userId}/backupProjects/`, 'saveDate')

    snapshot.forEach(element => {
      const result = element.val()
      if (result.deleted !== 1) {
        if (result.project_code) {
          result.key_backup = element.key
          result.project_code = result.project_code.replace('_', '')
          res.unshift(result)
        } else if (result.calcul_code) {
          //new way

          result.key_backup = element.key
          result.project_code = result.calcul_code.replace('_', '')
          res.unshift(result)
        }
      }
    })

    return res
  } catch (error) {
    sendError(error)
    throw error
  }
}

export async function fetchMailProfile(userId: string) {
  try {
    const snapshot = await once(`users/${userId}/mailProfile`)

    return snapshot.val()
  } catch (error) {
    sendError(error)
    throw error
  }
}

export async function updateMailProfile(userid: string, value: Object) {
  await update(`users/${userid}/mailProfile/`, value)
}

export async function updateUser(userId: string, value: Partial<UserState>) {
  try {
    await update(`users/${userId}/profile`, { ...value, lastModified: Math.floor(Date.now() / 1000) })
  } catch (error) {
    sendError(error, 'updateUser')
    throw error
  }
}

export async function firestoreSubscription(uid: string) {
  return await firestoreSubscriptionData(uid)
}

export async function updateFirestoreSubscription(uid: string, data: { [key: string]: string | number | boolean }) {
  try {
    await updateFirestoreSubscriptionData(uid, data)
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const firebaseError = error as any
    const errorCode = firebaseError?.code

    // If permission denied, user doesn't have rights to update/create - log but don't throw
    // This allows the app to continue functioning even if Firestore update fails
    if (errorCode === 'permission-denied') {
      sendError(error, `updateFirestoreSubscription:permission-denied - uid: ${uid}, data: ${JSON.stringify(data)}`)
      // Don't throw - app can continue without Firestore update
      return
    }

    // Handle case where document doesn't exist (not-found or failed-precondition)
    if (errorCode === 'not-found' || errorCode === 'failed-precondition' || firebaseError?.message?.includes('No document to update')) {
      // Try to create the document if it doesn't exist
      try {
        await setFirestoreSubscriptionData(uid, {
          expirationDate: 0,
          expirationDateStripe: 0,
          expirationDateDistributor: 0,
          isAndroid: false,
          isIos: false,
          isStripe: false,
          isDistributor: false,
          offerBy: '',
          offerBySubscriptionId: '',
          ...data
        })
      } catch (setError) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const setErrorCode = (setError as any)?.code
        // If setDoc also fails with permission-denied, log but don't throw
        if (setErrorCode === 'permission-denied') {
          sendError(setError, `updateFirestoreSubscription:setDoc:permission-denied - uid: ${uid}, data: ${JSON.stringify(data)}`)
          // Don't throw - app can continue without Firestore update
          return
        }
        // For other setDoc errors, log and rethrow the original update error
        sendError(setError, `updateFirestoreSubscription:setDoc - uid: ${uid}, data: ${JSON.stringify(data)}`)
        throw error
      }
    } else {
      // For other errors, log and rethrow
      sendError(error, `updateFirestoreSubscription:updateDoc - uid: ${uid}, data: ${JSON.stringify(data)}`)
      throw error
    }
  }
}

// ***** STORAGE *** ////
export async function uploadImageMail(userId: string, image: Blob, type: 'png' | 'jpeg') {
  try {
    const url = await uploadFile(`images_logo_mail/${userId}`, image, type)

    await updateMailProfile(userId, { logoUrl: url })
    return url
  } catch (error) {
    sendError(error, 'uploadImageMail')
    throw error
  }
}
